// src/screens/dm-activity/DMActivity.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Search, Filter, Instagram, Facebook, Music2, MoreVertical,
  CheckCircle2, Zap, ArrowRight, ArrowLeft, User, AlertCircle, Tag,
  Calendar, ChevronLeft, ChevronRight, Loader2, RefreshCw,
  PlugZap, Link2, Inbox, TrendingUp, DollarSign, Brain,
  Sparkles, Send, MessageCircle,
} from 'lucide-react';
import './DMActivity.css';
import { getApiUrl } from '../../config/api';
const PAGE_LIMIT = 25;

const getToken = () => localStorage.getItem('access_token') || '';
const authHeaders = () => {
  const token = getToken();
  return { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const saveCache = (platform, data) => {
  try {
    sessionStorage.setItem(`dm_conversations_${platform}`, JSON.stringify(data));
  } catch {}
};
const loadCache = (platform) => {
  try {
    const cached = sessionStorage.getItem(`dm_conversations_${platform}`);
    return cached ? JSON.parse(cached) : null;
  } catch { return null; }
};

const buildConversationsUrl = (platformFilter, limit, page) => {
  const offset = (page - 1) * limit;
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  switch (platformFilter) {
    case 'Facebook':  return getApiUrl(`/conversations/facebook?${qs}`);
    case 'Instagram': return getApiUrl(`/conversations/instagram?${qs}`);
    case 'TikTok':    return getApiUrl(`/conversations/tiktok?${qs}`);
    default:          return getApiUrl(`/conversations/?${qs}`);
  }
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value != null && Array.isArray(value.data)) return value.data;
  return [];
};

const extractConversationList = (payload) => {
  if (Array.isArray(payload)) return payload;
  for (const key of ['conversations', 'results', 'items', 'data']) {
    const v = payload[key];
    if (Array.isArray(v)) return v;
    if (v != null && Array.isArray(v.data)) return v.data;
  }
  return [];
};

const extractParticipantName = (rawParts) => {
  if (!rawParts?.length) return null;
  const human = rawParts.find(p => p?.name && !/^\d+$/.test(p.name));
  return human?.name || rawParts.find(p => p?.name)?.name || null;
};
const extractParticipantAvatar = (rawParts) => {
  if (!rawParts?.length) return null;
  const human = rawParts.find(p => p?.name && !/^\d+$/.test(p.name)) || rawParts[0];
  return human?.profile_pic || human?.pic || human?.picture || null;
};

const extractMessageText = (m) =>
  m.message || m.text || m.content || m.message_text || m.message_content || '';

/**
 * Determine message sender.
 *
 * The Facebook Conversations API returns messages where `from.id` is the
 * sender's Facebook ID. The page that owns the conversation has its own
 * numeric ID (e.g. "260049122536720"). When `from.id` matches the page ID
 * the message was sent BY the page (us / bot → right side). Otherwise it
 * was sent by the customer (left side).
 *
 * @param {object} m       - raw message object from the API
 * @param {string} pageId  - Facebook Page ID that owns this conversation
 */
const extractMessageSender = (m, pageId) => {
  // ── 1. Compare from.id to the known page ID ───────────────────────────
  if (pageId && m.from?.id) {
    return String(m.from.id) === String(pageId) ? 'revexia' : 'user';
  }

  // ── 2. from.email numeric alias — Facebook assigns pages an email like
  //       "260049122536720@facebook.com". Customers never get this format.
  const fromEmail = m.from?.email || '';
  if (/^\d{10,}@facebook\.com$/.test(fromEmail)) return 'revexia';

  // ── 3. Explicit direction / role / type fields ────────────────────────
  if (m.direction === 'incoming')  return 'user';
  if (m.direction === 'outgoing')  return 'revexia';
  if (m.role === 'user' || m.role === 'customer') return 'user';
  if (m.role === 'assistant' || m.role === 'bot' || m.role === 'agent') return 'revexia';
  if (m.type === 'received' || m.type === 'incoming') return 'user';
  if (m.type === 'sent'     || m.type === 'outgoing') return 'revexia';
  if (m.is_echo === true)  return 'revexia';
  if (m.is_echo === false) return 'user';
  if (m.sender_type === 'page' || m.sender_type === 'bot')      return 'revexia';
  if (m.sender_type === 'user' || m.sender_type === 'customer') return 'user';

  // ── 4. from.id present but pageId unknown → treat as customer ─────────
  if (m.from?.name || m.from?.id) return 'user';

  return 'user';
};

/**
 * @param {object} m       - raw message
 * @param {number} idx     - index for stable key
 * @param {string} convId  - conversation id
 * @param {string} pageId  - page/bot id for sender detection
 */
const mapOneMessage = (m, idx, convId, pageId) => ({
  id:        `msg_${convId}_${idx}`,
  sender:    extractMessageSender(m, pageId),
  text:      extractMessageText(m),
  timestamp: (m.created_time || m.timestamp)
    ? new Date(m.created_time || m.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : '',
  tags: Array.isArray(m.tags) ? m.tags : undefined,
  // Keep raw from.name so we can display it as sender label
  fromName: m.from?.name || null,
});

const detectIntent = (likelihood) => {
  if (likelihood >= 75) return 'High';
  if (likelihood >= 45) return 'Medium';
  return 'Low';
};

const detectPlatform = (conv) => {
  const src = (conv.platform || conv.source || conv.channel || '').toLowerCase();
  if (src.includes('instagram')) return 'instagram';
  if (src.includes('facebook'))  return 'facebook';
  if (src.includes('tiktok'))    return 'tiktok';
  if (src.includes('manychat'))  return 'facebook';
  return 'facebook';
};

/**
 * Extract the page's own ID.
 *
 * Priority:
 * 1. Explicit page_id / numeric sender_id on conversation
 * 2. Participants — numeric email prefix (e.g. 260049122536720@facebook.com)
 *    or numeric-only name
 * 3. Inline messages — from.email matching numeric@facebook.com pattern
 * 4. localStorage fallback (saved by Integrations after FB OAuth)
 */
const extractPageId = (raw) => {
  // 1. Explicit fields
  if (raw.page_id) return String(raw.page_id);
  if (raw.sender_id && /^\d{10,}$/.test(String(raw.sender_id)))
    return String(raw.sender_id);

  // 2. Participants list
  const parts = toArray(raw.participants);
  for (const p of parts) {
    if (!p) continue;
    const email = p.email || '';
    if (/^\d{10,}@/.test(email)) return String(p.id);
    if (p.name && /^\d{10,}$/.test(p.name)) return String(p.id);
  }

  // 3. Scan inline messages for FB page email alias (e.g. 260049122536720@facebook.com)
  const msgs = toArray(raw.messages);
  for (const m of msgs) {
    const email = m.from?.email || '';
    if (/^\d{10,}@facebook\.com$/.test(email)) return String(m.from.id);
  }

  // 4. localStorage — written by Integrations.js when FB status is confirmed
  const stored = localStorage.getItem('fb_page_id');
  if (stored) return stored;

  return null;
};

const mapConversation = (raw) => {
  const likelihood        = raw.purchase_likelihood ?? raw.likelihood ?? 0;
  const rawMessages       = toArray(raw.messages);
  const rawParts          = toArray(raw.participants);
  const participantName   = extractParticipantName(rawParts);
  const participantAvatar = extractParticipantAvatar(rawParts);
  const pageId            = extractPageId(raw);
  const messages  = rawMessages.map((m, idx) => mapOneMessage(m, idx, raw.id, pageId));
  const lastMsg   = messages[messages.length - 1];
  const snippetText = raw.last_message_text || raw.snippet || raw.last_message || raw.message_preview || lastMsg?.text || '';
  const displayName = raw.contact_name || raw.name || raw.user || raw.sender_name || participantName || null;
  const displayUsername = raw.username || raw.handle || (raw.sender_id && !/^\d{10,}$/.test(String(raw.sender_id)) ? raw.sender_id : null) || null;

  return {
    id:          String(raw.id),
    pageId,                        // ← stored so fetchMessages can use it
    user:        displayName || 'Instagram User',
    username:    displayUsername
                   ? (displayUsername.startsWith('@') ? displayUsername : `@${displayUsername}`)
                   : (participantName ? `@${participantName.toLowerCase().replace(/\s+/g, '_')}` : `@user_${String(raw.id).slice(-6)}`),
    avatar:      raw.avatar || raw.profile_pic || raw.profile_picture || participantAvatar || `https://picsum.photos/seed/${raw.id}/100/100`,
    platform:    detectPlatform(raw),
    lastMessage: snippetText,
    intent:      detectIntent(likelihood),
    time:        (raw.last_activity || raw.updated_time || raw.last_message_time)
                   ? new Date(raw.last_activity || raw.updated_time || raw.last_message_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                   : raw.time || '',
    likelihood,
    messages,
    journey: Array.isArray(raw.journey) ? raw.journey : [],
  };
};

const normalizeAnalysis = (data) => ({
  likelihood:     data.purchase_likelihood ?? data.likelihood ?? data.score ?? null,
  intent:         data.intent ?? data.intent_label ?? null,
  aov:            data.aov ?? data.average_order_value ?? data.aov_potential ?? null,
  suggestion:     data.suggestion ?? data.next_action ?? data.recommended_action ?? data.next_best_action ?? null,
  suggestionType: data.suggestion_type ?? data.action_type ?? null,
  summary:        data.summary ?? data.conversation_summary ?? null,
  topics:         Array.isArray(data.topics) ? data.topics : [],
  sentiment:      data.sentiment ?? null,
  raw:            data,
});

const isNoPlatformError = (body) =>
  typeof body?.detail === 'string' &&
  body.detail.toLowerCase().includes('no connected platforms');


/* ── Date Picker ───────────────────────────────────────────────────────────── */
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const DatePickerPopover = ({ selectedDate, onChange, onClose }) => {
  const [viewDate, setViewDate] = useState(new Date(selectedDate));
  const year = viewDate.getFullYear(), month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysCount = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysCount; d++) cells.push(d);
  const selDay = selectedDate.getDate(), selMonth = selectedDate.getMonth(), selYear = selectedDate.getFullYear();
  const isSelected = (d) => d === selDay && month === selMonth && year === selYear;
  const isToday    = (d) => { const t = new Date(); return d === t.getDate() && month === t.getMonth() && year === t.getFullYear(); };
  return (
    <div className="datepicker-popover">
      <div className="datepicker-nav">
        <button className="datepicker-nav-btn" onClick={() => setViewDate(new Date(year, month-1, 1))}><ChevronLeft size={14}/></button>
        <span className="datepicker-month-label">{MONTHS[month]} {year}</span>
        <button className="datepicker-nav-btn" onClick={() => setViewDate(new Date(year, month+1, 1))}><ChevronRight size={14}/></button>
      </div>
      <div className="datepicker-grid-header">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <span key={d} className="datepicker-day-name">{d}</span>)}
      </div>
      <div className="datepicker-grid">
        {cells.map((d, i) => (
          <button key={i} className={`datepicker-cell${!d?' empty':''}${d&&isSelected(d)?' selected':''}${d&&isToday(d)&&!isSelected(d)?' today':''}`}
            onClick={() => d && (onChange(new Date(year, month, d)), onClose())} disabled={!d}>{d||''}</button>
        ))}
      </div>
      <div className="datepicker-footer">
        <button className="datepicker-today-btn" onClick={() => { onChange(new Date()); onClose(); }}>Today</button>
      </div>
    </div>
  );
};

const InteractiveDateFilter = ({ onDateChange }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return (
    <div className="date-filter-wrapper" ref={ref}>
      <button className={`date-filter-btn${open?' open':''}`} onClick={() => setOpen(!open)}>
        <Calendar size={14}/><span>Today, {label}</span>
        <ChevronRight size={12} className={`date-chevron${open?' rotated':''}`}/>
      </button>
      {open && <DatePickerPopover selectedDate={date} onChange={(d) => { setDate(d); onDateChange?.(d); }} onClose={() => setOpen(false)}/>}
    </div>
  );
};


/* ── Empty / error state cards ─────────────────────────────────────────────── */
const platformMeta = {
  all:       { label: 'any platform', color: '#6B7280', icon: null },
  instagram: { label: 'Instagram',    color: '#d6249f', icon: <Instagram size={28} style={{ color: '#d6249f' }}/> },
  facebook:  { label: 'Facebook',     color: '#1877F2', icon: <Facebook  size={28} style={{ color: '#1877F2' }}/> },
  tiktok:    { label: 'TikTok',       color: '#00f2ea', icon: <Music2    size={28} style={{ color: '#00f2ea' }}/> },
};

const EmptyStateCard = ({ icon, title, message, actionLabel, onAction, accent = '#6B7280' }) => (
  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'2.5rem 2rem', gap:'0.85rem', minHeight:260 }}>
    <div style={{ width:60, height:60, borderRadius:'50%', background:`${accent}18`, border:`1px solid ${accent}35`, display:'flex', alignItems:'center', justifyContent:'center' }}>{icon}</div>
    <h4 style={{ fontSize:'0.88rem', fontWeight:700, color:'#e5e7eb', margin:0 }}>{title}</h4>
    <p style={{ fontSize:'0.77rem', color:'#6B7280', margin:0, maxWidth:300, lineHeight:1.65 }}>{message}</p>
    {onAction && actionLabel && (
      <button onClick={onAction} style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.45rem 0.9rem', background:accent, color:'#fff', border:'none', borderRadius:'999px', fontSize:'0.68rem', fontWeight:900, letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer' }}>
        {actionLabel}<ArrowRight size={12}/>
      </button>
    )}
  </div>
);

const NotConnectedState = ({ platform, onNavigate }) => {
  const meta = platformMeta[platform] || platformMeta.all;
  return <EmptyStateCard icon={meta.icon ?? <PlugZap size={26} style={{ color:'#6B7280' }}/>} title={meta.label==='any platform'?'No platform connected':`${meta.label} not connected`} message={meta.label==='any platform'?'Connect a platform from Integrations to start monitoring conversations.':`Connect your ${meta.label} account from Integrations to see DM activity.`} actionLabel="Go to Integrations" onAction={onNavigate} accent={meta.color}/>;
};
const NoConversationsState = ({ platform }) => {
  const meta = platformMeta[platform] || platformMeta.all;
  return <EmptyStateCard icon={<Inbox size={26} style={{ color:meta.color }}/>} title="No conversations yet" message={meta.label==='any platform'?'Your account is connected, but no conversations have synced yet.':`${meta.label} is connected, but no conversations have synced yet.`} accent={meta.color}/>;
};
const PageMismatchState = ({ onNavigate }) => (
  <EmptyStateCard icon={<Link2 size={26} style={{ color:'#F59E0B' }}/>} title="Page mismatch detected" message="The Facebook account you logged into does not manage the connected page. Log in with the correct Facebook profile." actionLabel="Go to Integrations" onAction={onNavigate} accent="#F59E0B"/>
);


/* ── Analyze Panel ─────────────────────────────────────────────────────────── */
const IntentBar = ({ value, color }) => (
  <div style={{ marginTop:'0.4rem' }}>
    <div style={{ height:3, borderRadius:4, background:'rgba(255,255,255,0.07)', overflow:'hidden' }}>
      <div style={{ height:'100%', width:`${value}%`, background:color, borderRadius:4, transition:'width 0.7s cubic-bezier(0.16,1,0.3,1)' }}/>
    </div>
  </div>
);

const SentimentChip = ({ sentiment }) => {
  const map = {
    positive: { color:'#10B981', bg:'rgba(16,185,129,0.12)', label:'Positive' },
    negative: { color:'#EF4444', bg:'rgba(239,68,68,0.12)',  label:'Negative' },
    neutral:  { color:'#6B7280', bg:'rgba(107,114,128,0.12)',label:'Neutral'  },
  };
  const s = map[(sentiment||'').toLowerCase()] || map.neutral;
  return (
    <span style={{ display:'inline-block', padding:'0.2rem 0.6rem', background:s.bg, color:s.color, borderRadius:'999px', fontSize:'0.63rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>
      {s.label}
    </span>
  );
};

const AnalyzePanel = ({ convId, analysis, loading: analysisLoading, notConnected: analyzeNotConnected }) => {
  const likelihood  = analysis?.likelihood ?? null;
  const intentLabel = analysis?.intent ? analysis.intent : likelihood !== null ? detectIntent(likelihood) : null;
  const intentColor = intentLabel === 'High' ? '#10B981' : intentLabel === 'Medium' ? '#F59E0B' : '#6B7280';
  const aov        = analysis?.aov;
  const suggestion = analysis?.suggestion;
  const summary    = analysis?.summary;
  const sentiment  = analysis?.sentiment;
  const topics     = analysis?.topics || [];

  if (analyzeNotConnected) {
    return (
      <div style={{ padding:'1rem' }}>
        <p style={{ fontSize:'0.72rem', color:'#6B7280', textAlign:'center', lineHeight:1.6 }}>
          Insights unavailable — no platform connected.
        </p>
      </div>
    );
  }

  return (
    <div className="analyze-panel-inner">
      <div className="insights-section">
        <h4 className="insights-title"><Brain size={12} style={{ marginRight:5, verticalAlign:'middle' }}/>Live Insights</h4>
        <div className="insights-cards">
          <div className="insight-card">
            <p className="insight-label">Purchase Likelihood</p>
            {analysisLoading
              ? <div style={{ height:13, width:'55%', background:'rgba(255,255,255,0.07)', borderRadius:4, marginTop:6 }}/>
              : likelihood !== null
                ? <><div className="insight-value-wrapper"><TrendingUp size={14} style={{ color:intentColor }}/><p className="insight-value" style={{ color:intentColor }}>{likelihood}%</p></div><IntentBar value={likelihood} color={intentColor}/></>
                : <p className="insight-value" style={{ color:'#6B7280', fontSize:'0.75rem' }}>—</p>}
          </div>
          <div className="insight-card">
            <p className="insight-label">Current Intent</p>
            {analysisLoading
              ? <div style={{ height:13, width:'40%', background:'rgba(255,255,255,0.07)', borderRadius:4, marginTop:6 }}/>
              : intentLabel
                ? <div className="insight-value-wrapper"><Zap size={14} style={{ color:intentColor }}/><p className="insight-value" style={{ color:intentColor }}>{intentLabel}</p></div>
                : <p className="insight-value" style={{ color:'#6B7280', fontSize:'0.75rem' }}>—</p>}
          </div>
          <div className="insight-card">
            <p className="insight-label">AOV Potential</p>
            {analysisLoading
              ? <div style={{ height:13, width:'50%', background:'rgba(255,255,255,0.07)', borderRadius:4, marginTop:6 }}/>
              : aov
                ? <div className="insight-value-wrapper"><DollarSign size={14} style={{ color:'#10B981' }}/><p className="insight-value" style={{ color:'#10B981' }}>{typeof aov === 'number' ? `$${aov.toFixed(2)}` : aov}</p></div>
                : <p className="insight-value" style={{ color:'#6B7280', fontSize:'0.75rem' }}>—</p>}
          </div>
          {(sentiment || analysisLoading) && (
            <div className="insight-card">
              <p className="insight-label">Sentiment</p>
              {analysisLoading
                ? <div style={{ height:22, width:'60%', background:'rgba(255,255,255,0.07)', borderRadius:999, marginTop:6 }}/>
                : <SentimentChip sentiment={sentiment}/>}
            </div>
          )}
        </div>
      </div>

      {(summary || analysisLoading) && (
        <div className="insights-section" style={{ marginTop:'1rem' }}>
          <h4 className="insights-title"><Sparkles size={12} style={{ marginRight:5, verticalAlign:'middle' }}/>Summary</h4>
          {analysisLoading
            ? <div style={{ display:'flex', flexDirection:'column', gap:6 }}>{[80,95,65].map((w,i) => <div key={i} style={{ height:9, width:`${w}%`, background:'rgba(255,255,255,0.07)', borderRadius:4 }}/>)}</div>
            : <p style={{ fontSize:'0.74rem', color:'#9CA3AF', lineHeight:1.65, margin:0, wordBreak:'break-word', overflowWrap:'break-word' }}>{summary}</p>}
        </div>
      )}

      {topics.length > 0 && (
        <div style={{ marginTop:'0.75rem', display:'flex', flexWrap:'wrap', gap:'0.3rem' }}>
          {topics.map((t, i) => (
            <span key={i} style={{ fontSize:'0.62rem', fontWeight:600, padding:'0.18rem 0.5rem', background:'rgba(255,255,255,0.05)', color:'#9CA3AF', borderRadius:'999px', border:'1px solid rgba(255,255,255,0.08)' }}>{t}</span>
          ))}
        </div>
      )}

      <div className="action-section" style={{ marginTop:'1rem' }}>
        <h4 className="insights-title"><Zap size={12} style={{ marginRight:5, verticalAlign:'middle' }}/>Next Best Action</h4>
        {analysisLoading
          ? <div className="ai-suggestion-card" style={{ opacity:0.5 }}><div style={{ display:'flex', flexDirection:'column', gap:8 }}><div style={{ height:9, width:'80%', background:'rgba(255,255,255,0.07)', borderRadius:4 }}/><div style={{ height:9, width:'65%', background:'rgba(255,255,255,0.07)', borderRadius:4 }}/></div></div>
          : suggestion
            ? <div className="ai-suggestion-card ai-suggestion-card--live"><div className="ai-suggestion-header"><Send size={13} style={{ color:'#6EE7B7' }}/><span style={{ color:'#6EE7B7' }}>AI Suggestion</span>{analysis?.suggestionType && <span style={{ marginLeft:'auto', fontSize:'0.58rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.07em', color:'#6B7280', background:'rgba(255,255,255,0.06)', padding:'0.12rem 0.4rem', borderRadius:3 }}>{analysis.suggestionType}</span>}</div><p className="ai-suggestion-text">"{suggestion}"</p><button className="execute-btn"><span>Execute Follow-up</span><ArrowRight size={13}/></button></div>
            : <div className="ai-suggestion-card"><div className="ai-suggestion-header"><AlertCircle size={13} style={{ color:'#6B7280' }}/><span style={{ color:'#6B7280' }}>No suggestion available</span></div><p className="ai-suggestion-text" style={{ color:'#4B5563' }}>The AI couldn't generate a suggestion yet.</p></div>}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════════════════════ */
const DMActivity = ({ onNavigateToIntegrations }) => {
  const [selectedId,         setSelectedId]         = useState(null);
  const [platformFilter,     setPlatformFilter]     = useState('All');
  const [activeIntentFilter, setActiveIntentFilter] = useState(null);
  const [searchQuery,        setSearchQuery]        = useState('');
  const [selectedDate,       setSelectedDate]       = useState(null);

  const [conversations,   setConversations]   = useState(() => loadCache('All') || []);
  const [loading,         setLoading]         = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [notConnected,    setNotConnected]    = useState(false);
  const [pageMismatch,    setPageMismatch]    = useState(false);
  const [pagination,      setPagination]      = useState({ page:1, hasMore:false, total:0 });
  const [mobileActiveView, setMobileActiveView] = useState('list');
  const [mobileDetailTab,  setMobileDetailTab]  = useState('chat');

  const analyzeMapRef = useRef({});
  const [analyzeMap, setAnalyzeMap] = useState({});

  const fetchingRef = useRef(false);

  const platformKey =
    platformFilter === 'All'       ? 'all'       :
    platformFilter === 'Instagram' ? 'instagram' :
    platformFilter === 'Facebook'  ? 'facebook'  :
    platformFilter === 'TikTok'    ? 'tiktok'    : 'all';


  const fetchConversations = useCallback(async (page = 1, { silent = false } = {}) => {
    if (fetchingRef.current && !silent) return;
    if (!getToken()) { setNotConnected(true); setLoading(false); return; }
    fetchingRef.current = true;
    if (!silent) setLoading(true);
    setNotConnected(false);
    try {
      const url = buildConversationsUrl(platformFilter, PAGE_LIMIT, page);
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) {
        setNotConnected(true);
        setConversations([]);
        setPagination({ page:1, hasMore:false, total:0 });
        return;
      }
      const payload = await res.json();
      const rawList = extractConversationList(payload);
      const mapped  = rawList.map((item) => {
        try { return mapConversation(item); } catch { return null; }
      }).filter(Boolean);

      setConversations(prev => {
        const updated = page === 1 ? mapped : [...prev, ...mapped];
        saveCache(platformFilter, updated);
        return updated;
      });
      const total   = payload.total ?? payload.count ?? rawList.length;
      const fetched = (page - 1) * PAGE_LIMIT + mapped.length;
      setPagination({ page, hasMore: fetched < total, total });
      if (page === 1) setSelectedId(prev => prev ?? mapped[0]?.id ?? null);
    } catch (err) {
      console.error('[DMActivity] fetchConversations:', err);
      setNotConnected(true);
      setConversations([]);
      setPagination({ page:1, hasMore:false, total:0 });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [platformFilter]);


  const fetchMessages = useCallback(async (convId) => {
    setLoadingMessages(true);
    try {
      const res = await fetch(getApiUrl(`/conversations/${convId}/messages?limit=50`), { headers: authHeaders() });
      if (!res.ok) return;
      const payload = await res.json();

      let raw = [];
      if (Array.isArray(payload))                                raw = payload;
      else if (Array.isArray(payload.data))                      raw = payload.data;
      else if (payload.data && Array.isArray(payload.data.data)) raw = payload.data.data;
      else if (payload.messages)                                 raw = toArray(payload.messages);

      // Log for debugging
      console.log(`[API] GET /conversations/${convId}/messages →`, { count: raw.length, sample: raw[0] });

      setConversations(prev => {
        // Retrieve the pageId stored on the conversation so sender detection works
        const conv = prev.find(c => c.id === convId);
        let pageId = conv?.pageId ?? null;

        // If pageId not known yet, try to derive it from the message emails.
        // FB page senders always have an email like "260049122536720@facebook.com".
        if (!pageId) {
          for (const m of raw) {
            const email = m.from?.email || '';
            if (/^\d{10,}@facebook\.com$/.test(email)) {
              pageId = String(m.from.id);
              // Persist so subsequent calls don't need to re-derive
              localStorage.setItem('fb_page_id', pageId);
              console.log(`[DMActivity] derived pageId from messages:`, pageId);
              break;
            }
          }
        }

        console.log(`[DMActivity] pageId for conv ${convId}:`, pageId);

        const messages = raw.map((m, idx) => mapOneMessage(m, idx, convId, pageId));
        const updated  = prev.map(c => c.id === convId ? { ...c, messages } : c);
        saveCache(platformFilter, updated);
        return updated;
      });
    } catch (err) { console.warn('[DMActivity] fetchMessages:', err); }
    finally { setLoadingMessages(false); }
  }, [platformFilter]);


  const analyzeConversation = useCallback(async (convId) => {
    const existing = analyzeMapRef.current[convId];
    if (existing?.data && !existing?.loading) return;

    const update = (patch) => {
      analyzeMapRef.current = { ...analyzeMapRef.current, [convId]: { ...analyzeMapRef.current[convId], ...patch } };
      setAnalyzeMap({ ...analyzeMapRef.current });
    };

    update({ data: existing?.data || null, loading: true, error: false });
    try {
      const res = await fetch(getApiUrl(`/conversations/${convId}/analyze`), { headers: authHeaders() });
      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch {}
        if (res.status === 500 && isNoPlatformError(body)) {
          setNotConnected(true);
          update({ data: null, loading: false, error: true, noPlatform: true });
          return;
        }
        update({ data: null, loading: false, error: true });
        return;
      }
      const raw  = await res.json();
      const norm = normalizeAnalysis(raw);
      if (norm.likelihood !== null) {
        setConversations(prev => prev.map(c => c.id === convId ? { ...c, likelihood: norm.likelihood, intent: detectIntent(norm.likelihood) } : c));
      }
      update({ data: norm, loading: false, error: false });
    } catch (err) {
      console.warn('[DMActivity] analyzeConversation:', err);
      update({ data: null, loading: false, error: true });
    }
  }, []);


  useEffect(() => {
    const cached = loadCache(platformFilter);
    if (cached && cached.length > 0) {
      setConversations(cached);
      setSelectedId(prev => prev ?? cached[0]?.id ?? null);
      setLoading(false);
      fetchConversations(1, { silent: true });
    } else {
      setConversations([]);
      setSelectedId(null);
      analyzeMapRef.current = {};
      setAnalyzeMap({});
      fetchConversations(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformFilter]);

  useEffect(() => {
    if (selectedId) {
      fetchMessages(selectedId);
      analyzeConversation(selectedId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (sessionStorage.getItem('fb_page_mismatch') === '1') setPageMismatch(true);
  }, []);


  const filteredConversations = conversations.filter(conv => {
    const platformMatch = platformFilter === 'All' || conv.platform === platformFilter.toLowerCase();
    const q = searchQuery.toLowerCase();
    const searchMatch = !q || conv.user.toLowerCase().includes(q) || conv.lastMessage.toLowerCase().includes(q) || conv.username.toLowerCase().includes(q);
    const dateMatch = !selectedDate || !conv.last_activity || new Date(conv.last_activity).toDateString() === selectedDate.toDateString();
    const baseMatch = platformMatch && searchMatch && dateMatch;
    if (activeIntentFilter === 'High Intent')   return baseMatch && conv.intent === 'High';
    if (activeIntentFilter === 'Medium Intent') return baseMatch && conv.intent === 'Medium';
    if (activeIntentFilter === 'Low Intent')    return baseMatch && conv.intent === 'Low';
    return baseMatch;
  });

  const selectedConv = conversations.find(c => c.id === selectedId) || null;
  const currentAnalyze = selectedId ? (analyzeMap[selectedId] || { data: null, loading: false, error: false }) : { data: null, loading: false, error: false };
  const analyzeNotConnected = currentAnalyze.noPlatform || false;

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'instagram': return <Instagram size={11} className="platform-icon instagram"/>;
      case 'tiktok':    return <Music2    size={11} className="platform-icon tiktok"   />;
      case 'facebook':  return <Facebook  size={11} className="platform-icon facebook" />;
      default:          return null;
    }
  };

  const platformTabs = [
    { label:'All',       disabled:false },
    { label:'Instagram', disabled:false },
    { label:'TikTok',    disabled:true  },
    { label:'Facebook',  disabled:false },
  ];


  return (
    <>
      <Helmet>
        <title>DM Activity - Revexia AI Revenue Brain</title>
        <meta name="description" content="Monitor high-intent customer conversations"/>
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        @keyframes mcSpin { to { transform: rotate(360deg); } }
        @keyframes activePulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.4)} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideInLeft { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }

        .dm-activity-wrapper { width:100%; box-sizing:border-box; overflow-x:hidden; font-family:'Plus Jakarta Sans', sans-serif; }
        .dm-activity-wrapper, .dm-activity-wrapper * { box-sizing:border-box; }

        .platform-icon.instagram { color:#E4405F; }
        .platform-icon.tiktok    { color:#22D3EE; }
        .platform-icon.facebook  { color:#3B82F6; }

        /* ── Header ── */
        .dm-activity-header {
          display:flex; align-items:center; justify-content:space-between;
          flex-wrap:wrap; gap:0.6rem; padding:0.75rem 0 1rem;
        }
        .header-description {
          font-size:0.72rem; color:#6B7280; margin:0;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
          max-width:280px; flex-shrink:0; letter-spacing:0.01em;
        }
        .header-actions {
          display:flex; align-items:center; gap:0.4rem;
          flex-wrap:nowrap; flex-shrink:1; min-width:0;
        }
        .platform-filter-tabs {
          display:flex; align-items:center;
          background:rgba(255,255,255,0.03);
          border:1px solid rgba(255,255,255,0.07);
          border-radius:9px; padding:3px; flex-shrink:0;
        }
        .platform-tab {
          padding:0.26rem 0.65rem; font-size:0.67rem; font-weight:600;
          color:#6B7280; background:none; border:none; border-radius:6px;
          cursor:pointer; white-space:nowrap; display:flex; align-items:center; gap:3px;
          transition:color 0.15s, background 0.15s;
        }
        .platform-tab:hover:not(:disabled) { color:#e5e7eb; }
        .platform-tab.active {
          background:linear-gradient(135deg,rgba(99,102,241,0.22),rgba(139,92,246,0.15));
          color:#c4b5fd; font-weight:700;
          border:1px solid rgba(139,92,246,0.25);
          box-shadow:0 1px 8px rgba(99,102,241,0.15);
        }
        .platform-tab.disabled-tab { opacity:0.35; cursor:not-allowed; }
        .tab-coming-soon {
          font-size:0.48rem; font-weight:800; letter-spacing:0.07em; text-transform:uppercase;
          background:rgba(239,68,68,0.18); color:#f87171; border-radius:3px; padding:1px 3px;
        }
        .filter-icon-btn {
          display:flex; align-items:center; justify-content:center;
          width:32px; height:32px;
          background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
          border-radius:8px; color:#6B7280; cursor:pointer; flex-shrink:0;
          transition:color 0.15s, background 0.15s, border-color 0.15s;
        }
        .filter-icon-btn:hover:not(:disabled) { color:#e5e7eb; background:rgba(255,255,255,0.08); border-color:rgba(255,255,255,0.12); }
        .filter-icon-btn:disabled { opacity:0.4; cursor:not-allowed; }

        /* ── Grid Layout ── */
        .dm-inbox-layout {
          display:grid;
          grid-template-columns:340px minmax(0, 1fr);
          gap:1rem;
          align-items:start;
          width:100%;
        }
        @media (max-width:1100px) { .dm-inbox-layout { grid-template-columns:290px minmax(0, 1fr); } }
        @media (max-width:860px)  {
          .dm-inbox-layout { grid-template-columns:minmax(0, 1fr); }
        }

        /* ── Sidebar ── */
        .conversations-sidebar {
          background:rgba(255,255,255,0.015);
          border:1px solid rgba(255,255,255,0.07);
          border-radius:16px; overflow:hidden;
          display:flex; flex-direction:column;
          height:calc(100vh - 60px); min-height:500px;
          box-shadow:0 4px 24px rgba(0,0,0,0.25);
        }
        .search-wrapper {
          position:relative; padding:0.75rem 0.75rem 0; flex-shrink:0;
        }
        .search-icon {
          position:absolute; left:1.25rem; top:50%; transform:translateY(-8%);
          color:#4B5563; pointer-events:none;
        }
        .search-input {
          width:100%; padding:0.52rem 0.75rem 0.52rem 2.4rem;
          background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08);
          border-radius:9px; color:#e5e7eb; font-size:0.77rem; outline:none;
          box-sizing:border-box; transition:border-color 0.15s;
          font-family:'Plus Jakarta Sans', sans-serif;
        }
        .search-input::placeholder { color:#374151; }
        .search-input:focus { border-color:rgba(139,92,246,0.4); background:rgba(139,92,246,0.04); }

        .filter-pills {
          display:flex; gap:0.35rem; padding:0.55rem 0.75rem;
          flex-shrink:0; flex-wrap:wrap;
        }
        .filter-pill {
          padding:0.18rem 0.6rem; font-size:0.62rem; font-weight:700;
          border-radius:999px; border:1px solid transparent; cursor:pointer;
          transition:all 0.15s; text-transform:uppercase; letter-spacing:0.05em;
          font-family:'Plus Jakarta Sans', sans-serif;
        }
        .filter-pill.high-intent { background:rgba(16,185,129,0.08); color:#6EE7B7; border-color:rgba(16,185,129,0.18); }
        .filter-pill.high-intent.active { background:rgba(16,185,129,0.22); border-color:#10B981; box-shadow:0 0 8px rgba(16,185,129,0.15); }
        .filter-pill.offer-req  { background:rgba(245,158,11,0.08);  color:#FCD34D; border-color:rgba(245,158,11,0.18); }
        .filter-pill.offer-req.active  { background:rgba(245,158,11,0.22); border-color:#F59E0B; box-shadow:0 0 8px rgba(245,158,11,0.15); }
        .filter-pill.unread     { background:rgba(107,114,128,0.08); color:#9CA3AF; border-color:rgba(107,114,128,0.18); }
        .filter-pill.unread.active     { background:rgba(107,114,128,0.22); border-color:#6B7280; }

        .sidebar-count-label {
          padding: 0.3rem 0.75rem 0.4rem;
          font-size: 0.6rem; font-weight: 700; color: #374151;
          text-transform: uppercase; letter-spacing: 0.1em; flex-shrink: 0;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .conversations-list {
          flex:1; overflow-y:auto; padding:0.3rem 0 0.5rem;
        }
        .conversations-list::-webkit-scrollbar { width:3px; }
        .conversations-list::-webkit-scrollbar-track { background:transparent; }
        .conversations-list::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.07); border-radius:4px; }

        .no-results { text-align:center; padding:2rem 1rem; color:#4B5563; font-size:0.77rem; }

        /* ── Detail Panel ── */
        .detail-panel {
          background:rgba(255,255,255,0.015);
          border:1px solid rgba(255,255,255,0.07);
          border-radius:16px; overflow:hidden;
          display:flex; flex-direction:column;
          height:calc(100vh - 60px); min-height:500px;
          box-shadow:0 4px 24px rgba(0,0,0,0.25);
          width:100%; min-width:0;
        }
        .detail-header {
          display:flex; align-items:center; justify-content:space-between;
          padding:0.85rem 1rem; border-bottom:1px solid rgba(255,255,255,0.06);
          flex-shrink:0; gap:0.5rem; min-width:0;
          background:rgba(255,255,255,0.015);
        }
        .detail-header-left { display:flex; align-items:center; gap:0.65rem; min-width:0; flex:1; }
        .detail-user-icon {
          width:40px; height:40px; border-radius:50%; flex-shrink:0;
          background:rgba(255,255,255,0.07); overflow:hidden;
          display:flex; align-items:center; justify-content:center; color:#6B7280;
          border:2px solid rgba(255,255,255,0.08);
        }
        .detail-user-info { min-width:0; }
        .detail-user-name { font-size:0.88rem; font-weight:700; color:#ffffff; margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .detail-user-meta { font-size:0.68rem; color:#4B5563; margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-top:1px; }
        .detail-header-right { display:flex; align-items:center; gap:0.75rem; flex-shrink:0; }
        .likelihood-display { text-align:right; }
        .likelihood-label { font-size:0.58rem; color:#4B5563; margin:0; text-transform:uppercase; letter-spacing:0.07em; }
        .likelihood-value { font-size:1.05rem; font-weight:900; margin:0; line-height:1.2; }
        .likelihood-value.high   { color:#10B981; }
        .likelihood-value.medium { color:#F59E0B; }
        .header-divider { width:1px; height:30px; background:rgba(255,255,255,0.07); }
        .more-options-btn { color:#4B5563; background:none; border:none; cursor:pointer; display:flex; padding:4px; border-radius:6px; transition:color 0.15s; }
        .more-options-btn:hover { color:#e5e7eb; }

        /* ── Detail Body ── */
        .detail-body {
          display:grid;
          grid-template-columns:minmax(0, 1fr) 280px;
          flex:1; overflow:hidden; min-height:0;
          width:100%; min-width:0;
        }
        @media (max-width:1300px) { .detail-body { grid-template-columns:minmax(0, 1fr) 260px; } }
        @media (max-width:1100px) { .detail-body { grid-template-columns:minmax(0, 1fr) 240px; } }
        @media (max-width:860px)  {
          .detail-body { grid-template-columns:minmax(0, 1fr); }
        }

        /* ── Chat thread ── */
        .chat-thread {
          padding:1rem; overflow-y:auto; display:flex; flex-direction:column; gap:0.65rem;
          border-right:1px solid rgba(255,255,255,0.05);
          min-width:0; width:100%;
        }
        .chat-thread::-webkit-scrollbar { width:3px; }
        .chat-thread::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.07); border-radius:4px; }

        /* ── Insights sidebar ── */
        .insights-sidebar {
          overflow-y:auto; overflow-x:hidden; padding:0.85rem 0.85rem;
          background:rgba(0,0,0,0.12);
          display:flex; flex-direction:column;
          border-left:1px solid rgba(255,255,255,0.05);
          width:100%; max-width:100%; min-width:0;
        }
        .insights-sidebar::-webkit-scrollbar { width:3px; }
        .insights-sidebar::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.06); border-radius:3px; }
        @media (max-width:860px) {
          .chat-thread { border-right:none; border-bottom:1px solid rgba(255,255,255,0.05); }
          .insights-sidebar { border-left:none; border-top:1px solid rgba(255,255,255,0.05); }
        }
        .analyze-panel-inner { display:flex; flex-direction:column; width:100%; max-width:100%; min-width:0; }
        .insights-section { margin-bottom:0.2rem; width:100%; min-width:0; }
        .insights-title {
          font-size:0.65rem; font-weight:800; text-transform:uppercase;
          letter-spacing:0.11em; color:#4B5563; margin:0 0 0.55rem;
          display:flex; align-items:center;
        }
        .insights-cards { display:flex; flex-direction:column; gap:0.45rem; width:100%; min-width:0; }
        .insight-card {
          background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06);
          border-radius:10px; padding:0.6rem 0.75rem;
          transition:border-color 0.15s;
          width:100%; max-width:100%; min-width:0;
        }
        .insight-card:hover { border-color:rgba(255,255,255,0.1); }
        .insight-label { font-size:0.62rem; color:#4B5563; margin:0 0 0.28rem; text-transform:uppercase; letter-spacing:0.06em; }
        .insight-value-wrapper { display:flex; align-items:center; gap:0.38rem; min-width:0; }
        .insight-value { font-size:0.95rem; font-weight:800; color:#ffffff; margin:0; }
        .action-section { margin-top:0.2rem; width:100%; min-width:0; }
        .ai-suggestion-card {
          background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.07);
          border-radius:10px; padding:0.75rem;
          width:100%; max-width:100%; min-width:0;
          word-break:break-word; overflow-wrap:break-word;
        }
        .ai-suggestion-card--live { border-color:rgba(110,231,183,0.18); background:rgba(16,185,129,0.04); }
        .ai-suggestion-header {
          display:flex; align-items:center; gap:0.38rem; margin-bottom:0.45rem;
          font-size:0.65rem; font-weight:700; color:#9CA3AF; text-transform:uppercase;
          letter-spacing:0.07em; min-width:0;
        }
        .ai-suggestion-text {
          font-size:0.73rem; color:#D1D5DB; line-height:1.6;
          margin:0 0 0.6rem; font-style:italic;
          word-break:break-word; overflow-wrap:break-word;
        }
        .execute-btn {
          display:flex; align-items:center; justify-content:center; gap:0.4rem; width:100%;
          padding:0.42rem; background:rgba(16,185,129,0.12); border:1px solid rgba(16,185,129,0.22);
          border-radius:8px; color:#6EE7B7; font-size:0.67rem; font-weight:700;
          cursor:pointer; transition:all 0.15s;
          font-family:'Plus Jakarta Sans', sans-serif;
        }
        .execute-btn:hover { background:rgba(16,185,129,0.22); border-color:rgba(16,185,129,0.38); }

        /* ── Journey Timeline ── */
        .journey-timeline {
          padding:0.75rem 1rem; border-top:1px solid rgba(255,255,255,0.06);
          flex-shrink:0; background:rgba(255,255,255,0.01);
        }
        .journey-timeline-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:0.75rem; }
        .journey-title { font-size:0.64rem; font-weight:800; text-transform:uppercase; letter-spacing:0.1em; color:#4B5563; margin:0; }
        .optimized-badge { display:flex; align-items:center; gap:0.3rem; font-size:0.6rem; color:#6EE7B7; font-weight:600; }
        .journey-steps { display:flex; align-items:flex-start; justify-content:space-between; gap:0.5rem; position:relative; }
        .journey-progress-line { position:absolute; top:11px; left:0; right:0; height:1px; background:rgba(255,255,255,0.06); z-index:0; }
        .journey-step { display:flex; flex-direction:column; align-items:center; gap:0.28rem; flex:1; position:relative; z-index:1; }
        .journey-step-icon { width:22px; height:22px; border-radius:50%; display:flex; align-items:center; justify-content:center; }
        .journey-step-icon.completed { background:rgba(16,185,129,0.18); color:#6EE7B7; border:1px solid rgba(16,185,129,0.3); }
        .journey-step-icon.pending   { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1); }
        .journey-step-dot { width:7px; height:7px; border-radius:50%; background:rgba(255,255,255,0.18); }
        .journey-step-info { text-align:center; }
        .journey-step-name { font-size:0.58rem; font-weight:600; margin:0; }
        .journey-step-name.completed { color:#6EE7B7; }
        .journey-step-name.pending   { color:#374151; }
        .journey-step-time { font-size:0.55rem; color:#4B5563; margin:0; }

        /* ── Date Picker ── */
        .date-filter-wrapper { position:relative; }
        .date-filter-btn {
          display:flex; align-items:center; gap:0.38rem; padding:0.3rem 0.6rem;
          background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
          border-radius:8px; color:#9CA3AF; font-size:0.68rem; cursor:pointer; white-space:nowrap;
          transition:all 0.15s; font-family:'Plus Jakarta Sans', sans-serif;
        }
        .date-filter-btn:hover, .date-filter-btn.open { color:#e5e7eb; border-color:rgba(255,255,255,0.14); }
        .date-chevron { transition:transform 0.2s; }
        .date-chevron.rotated { transform:rotate(90deg); }
        .datepicker-popover {
          position:absolute; top:calc(100% + 6px); right:0; z-index:200;
          background:#13141f; border:1px solid rgba(255,255,255,0.1); border-radius:12px;
          padding:0.75rem; width:216px; box-shadow:0 16px 48px rgba(0,0,0,0.6);
        }
        .datepicker-nav { display:flex; align-items:center; justify-content:space-between; margin-bottom:0.6rem; }
        .datepicker-nav-btn { background:none; border:none; color:#9CA3AF; cursor:pointer; padding:2px; border-radius:4px; display:flex; transition:color 0.12s; }
        .datepicker-nav-btn:hover { color:#fff; }
        .datepicker-month-label { font-size:0.73rem; font-weight:700; color:#e5e7eb; }
        .datepicker-grid-header { display:grid; grid-template-columns:repeat(7,1fr); margin-bottom:0.28rem; }
        .datepicker-day-name { font-size:0.58rem; color:#4B5563; text-align:center; font-weight:600; }
        .datepicker-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:1px; }
        .datepicker-cell {
          aspect-ratio:1; display:flex; align-items:center; justify-content:center;
          font-size:0.67rem; color:#9CA3AF; background:none; border:none; cursor:pointer;
          border-radius:6px; transition:all 0.1s;
        }
        .datepicker-cell:hover:not(.empty):not(:disabled) { background:rgba(255,255,255,0.08); color:#fff; }
        .datepicker-cell.selected { background:rgba(139,92,246,0.3); color:#c4b5fd; font-weight:700; }
        .datepicker-cell.today { color:#6EE7B7; font-weight:700; }
        .datepicker-cell.empty { cursor:default; }
        .datepicker-footer { margin-top:0.5rem; text-align:center; }
        .datepicker-today-btn { font-size:0.63rem; color:#9CA3AF; background:none; border:none; cursor:pointer; padding:4px 8px; border-radius:4px; transition:all 0.12s; font-family:'Plus Jakarta Sans', sans-serif; }
        .datepicker-today-btn:hover { color:#fff; background:rgba(255,255,255,0.06); }

        /* ── Desktop Overrides for Mobile-Specific Classes ── */
        @media (min-width: 861px) {
          .conversations-sidebar.mobile-hidden {
            display: flex !important;
          }
          .detail-panel-wrapper.mobile-hidden {
            display: block !important;
          }
          .mobile-back-btn {
            display: none !important;
          }
          .mobile-detail-tabs {
            display: none !important;
          }
          .chat-thread.mobile-tab-hidden {
            display: flex !important;
          }
          .insights-sidebar.mobile-tab-hidden {
            display: flex !important;
          }
        }

        /* ── Mobile & Tablet Responsiveness (<= 860px) ── */
        @media (max-width: 860px) {
          .dm-inbox-layout {
            grid-template-columns: 1fr;
            gap: 0;
          }
          .conversations-sidebar.mobile-hidden {
            display: none !important;
          }
          .detail-panel-wrapper.mobile-hidden {
            display: none !important;
          }
          .mobile-back-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.09);
            color: #e5e7eb;
            cursor: pointer;
            flex-shrink: 0;
            transition: background 0.15s, color 0.15s;
            margin-right: 4px;
            padding: 0;
          }
          .mobile-back-btn:hover {
            background: rgba(255, 255, 255, 0.12);
            color: #fff;
          }
          .mobile-detail-tabs {
            display: flex;
            align-items: center;
            background: rgba(0, 0, 0, 0.24);
            border-bottom: 1px solid rgba(255, 255, 255, 0.06);
            padding: 6px 10px;
            gap: 8px;
            flex-shrink: 0;
          }
          .mobile-detail-tab {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 7px 10px;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.06);
            border-radius: 8px;
            color: #9CA3AF;
            font-size: 0.72rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.15s;
            font-family: 'Plus Jakarta Sans', sans-serif;
          }
          .mobile-detail-tab:hover {
            background: rgba(255, 255, 255, 0.06);
            color: #e5e7eb;
          }
          .mobile-detail-tab.active {
            background: linear-gradient(135deg, rgba(99, 102, 241, 0.25), rgba(139, 92, 246, 0.2));
            border-color: rgba(139, 92, 246, 0.35);
            color: #c4b5fd;
            font-weight: 700;
          }
          .mobile-tab-badge {
            background: rgba(139, 92, 246, 0.25);
            color: #a78bfa;
            font-size: 0.6rem;
            padding: 1px 5px;
            border-radius: 4px;
            font-weight: 800;
          }
          .detail-body {
            grid-template-columns: 1fr;
            height: calc(100% - 110px);
          }
          .chat-thread.mobile-tab-hidden {
            display: none !important;
          }
          .insights-sidebar.mobile-tab-hidden {
            display: none !important;
          }
          .chat-thread {
            border-right: none;
            height: 100%;
          }
          .insights-sidebar {
            border-left: none;
            height: 100%;
            padding: 1rem 0.85rem;
          }
          .conversations-sidebar,
          .detail-panel {
            height: calc(100dvh - 130px);
            min-height: 480px;
          }
        }

        /* ── Mobile Devices (<= 768px) ── */
        @media (max-width: 768px) {
          .dm-activity-wrapper .container-fluid {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }
          .dm-activity-header {
            flex-direction: column;
            align-items: stretch;
            gap: 0.65rem;
            padding: 0.5rem 0 0.75rem;
          }
          .header-description {
            max-width: 100%;
          }
          .header-actions {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 4px;
            scrollbar-width: none;
          }
          .header-actions::-webkit-scrollbar {
            display: none;
          }
        }

        /* ── Small Mobile Phones (<= 640px) ── */
        @media (max-width: 640px) {
          .dm-activity-wrapper .container-fluid {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }
          .conversations-sidebar,
          .detail-panel {
            border-radius: 12px;
            height: calc(100dvh - 120px);
          }
          .detail-header {
            padding: 0.65rem 0.75rem;
          }
          .detail-user-icon {
            width: 34px;
            height: 34px;
          }
          .detail-user-name {
            font-size: 0.82rem;
          }
          .detail-user-meta {
            font-size: 0.62rem;
          }
          .likelihood-label {
            display: none;
          }
          .likelihood-value {
            font-size: 0.95rem;
          }
          .journey-timeline {
            padding: 0.6rem 0.75rem;
          }
          .journey-steps {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 6px;
            scrollbar-width: none;
          }
          .journey-steps::-webkit-scrollbar {
            display: none;
          }
          .journey-step {
            min-width: 80px;
            flex-shrink: 0;
          }
        }

        /* ── Extra Small Phones (<= 420px) ── */
        @media (max-width: 420px) {
          .detail-header-right {
            gap: 0.4rem;
          }
          .header-divider {
            display: none;
          }
          .detail-user-name {
            max-width: 120px;
          }
        }
      `}</style>

      <div className="dm-activity-wrapper">
        <div className="container-fluid px-4">

          {/* ── Header ── */}
          <header className="dm-activity-header">
            <p className="header-description">
              {loading && conversations.length === 0
                ? 'Loading conversations…'
                : pageMismatch
                  ? 'Facebook page mismatch detected'
                  : notConnected
                    ? 'Connect a platform to start'
                    : conversations.length === 0
                      ? 'Connected — no conversations yet'
                      : `Monitoring ${filteredConversations.length} high-intent threads`}
            </p>
            <div className="header-actions">
              <InteractiveDateFilter onDateChange={setSelectedDate}/>
              <div className="platform-filter-tabs">
                {platformTabs.map(({ label, disabled }) => (
                  <button key={label} onClick={() => !disabled && setPlatformFilter(label)} disabled={disabled}
                    title={disabled ? 'Coming soon' : undefined}
                    className={`platform-tab${!disabled && platformFilter===label?' active':''}${disabled?' disabled-tab':''}`}>
                    {label}
                    {disabled && <span className="tab-coming-soon">soon</span>}
                  </button>
                ))}
              </div>
              <button className="filter-icon-btn" onClick={() => fetchConversations(1)} title="Refresh" disabled={loading && conversations.length === 0}>
                {loading && conversations.length === 0
                  ? <Loader2 size={15} style={{ animation:'mcSpin 0.8s linear infinite' }}/>
                  : <RefreshCw size={15}/>}
              </button>
              <button className="filter-icon-btn"><Filter size={15}/></button>
            </div>
          </header>

          {/* ── Inbox Layout ── */}
          <div className="dm-inbox-layout">

            {/* Left Sidebar */}
            <div className={`conversations-sidebar ${mobileActiveView === 'detail' ? 'mobile-hidden' : ''}`}>
              <div className="search-wrapper">
                <Search className="search-icon" size={15}/>
                <input type="text" placeholder="Search conversations..." className="search-input"
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}/>
              </div>
              <div className="filter-pills">
                {[{key:'High Intent',cls:'high-intent'},{key:'Medium Intent',cls:'offer-req'},{key:'Low Intent',cls:'unread'}].map(({key,cls}) => (
                  <button key={key} onClick={() => setActiveIntentFilter(activeIntentFilter===key?null:key)}
                    className={`filter-pill ${cls}${activeIntentFilter===key?' active':''}`}>{key}</button>
                ))}
              </div>

              {!loading && !notConnected && !pageMismatch && filteredConversations.length > 0 && (
                <div className="sidebar-count-label">
                  {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''}
                </div>
              )}

              <div className="conversations-list">
                {/* Loading skeletons */}
                {loading && conversations.length === 0 && Array.from({length:6}).map((_,i) => (
                  <div key={i} style={{ padding:'10px 14px 10px 12px', borderBottom:'1px solid rgba(255,255,255,0.04)', opacity:0.3, animationDelay:`${i*0.05}s` }}>
                    <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                      <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(255,255,255,0.07)', flexShrink:0 }}/>
                      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:7 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <div style={{ height:10, width:'45%', background:'rgba(255,255,255,0.07)', borderRadius:4,
                            backgroundImage:'linear-gradient(90deg,rgba(255,255,255,0.06) 25%,rgba(255,255,255,0.12) 50%,rgba(255,255,255,0.06) 75%)',
                            backgroundSize:'200% 100%', animation:'shimmer 1.6s ease-in-out infinite' }}/>
                          <div style={{ height:8, width:'18%', background:'rgba(255,255,255,0.05)', borderRadius:4 }}/>
                        </div>
                        <div style={{ height:8, width:'30%', background:'rgba(255,255,255,0.04)', borderRadius:4 }}/>
                        <div style={{ height:8, width:'78%', background:'rgba(255,255,255,0.04)', borderRadius:4 }}/>
                        <div style={{ height:16, width:'38%', background:'rgba(255,255,255,0.04)', borderRadius:999 }}/>
                      </div>
                    </div>
                  </div>
                ))}

                {!loading && pageMismatch    && <PageMismatchState     onNavigate={onNavigateToIntegrations}/>}
                {!loading && !pageMismatch && notConnected && <NotConnectedState platform={platformKey} onNavigate={onNavigateToIntegrations}/>}
                {!loading && !pageMismatch && !notConnected && conversations.length === 0 && <NoConversationsState platform={platformKey}/>}

                {!pageMismatch && !notConnected && filteredConversations.map((conv, idx) => {
                  const isActive   = selectedId === conv.id;
                  const dotColor   = conv.intent === 'High' ? '#10B981' : conv.intent === 'Medium' ? '#F59E0B' : '#4B5563';
                  const badgeBg    = conv.intent === 'High' ? 'rgba(16,185,129,0.1)' : conv.intent === 'Medium' ? 'rgba(245,158,11,0.1)' : 'rgba(107,114,128,0.08)';
                  const badgeColor = conv.intent === 'High' ? '#6EE7B7' : conv.intent === 'Medium' ? '#FCD34D' : '#6B7280';
                  const badgeBorder= conv.intent === 'High' ? 'rgba(16,185,129,0.25)' : conv.intent === 'Medium' ? 'rgba(245,158,11,0.25)' : 'rgba(107,114,128,0.15)';
                  const leftBorder = isActive ? 'rgba(139,92,246,0.7)' : conv.intent === 'High' ? 'rgba(16,185,129,0.3)' : conv.intent === 'Medium' ? 'rgba(245,158,11,0.25)' : 'transparent';
                  const itemBg     = isActive ? 'linear-gradient(100deg,rgba(99,102,241,0.1),rgba(139,92,246,0.05))' : 'transparent';
                  const likelyColor= conv.intent === 'High' ? '#10B981' : conv.intent === 'Medium' ? '#F59E0B' : '#4B5563';
                  const likelyBg   = conv.intent === 'High' ? 'rgba(16,185,129,0.08)' : conv.intent === 'Medium' ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.04)';

                  return (
                    <div key={conv.id}
                      onClick={() => {
                        setSelectedId(conv.id);
                        setMobileActiveView('detail');
                      }}
                      style={{
                        padding: '10px 14px 10px 12px', cursor: 'pointer',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        borderLeft: `2px solid ${leftBorder}`,
                        background: itemBg, transition: 'background 0.15s',
                        animationDelay: `${idx * 0.03}s`,
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                        <div style={{ position:'relative', flexShrink:0 }}>
                          <img src={conv.avatar} alt={conv.user}
                            onError={e => { e.target.src=`https://picsum.photos/seed/${conv.id}/100/100`; }}
                            style={{ width:40, height:40, borderRadius:'50%', objectFit:'cover',
                              border: isActive ? '1.5px solid rgba(139,92,246,0.45)' : '1.5px solid rgba(255,255,255,0.09)',
                              display:'block', background:'rgba(255,255,255,0.07)' }}/>
                          <div style={{ position:'absolute', bottom:-2, right:-2, width:16, height:16,
                            background:'#0d0e1a', borderRadius:'50%', border:'1.5px solid rgba(255,255,255,0.07)',
                            display:'flex', alignItems:'center', justifyContent:'center' }}>
                            {getPlatformIcon(conv.platform)}
                          </div>
                        </div>
                        <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:'3px' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <span style={{ fontSize:'0.82rem', fontWeight:700, color: isActive ? '#fff' : '#e2e8f0',
                              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'160px', letterSpacing:'-0.01em' }}>
                              {conv.user}
                            </span>
                            <span style={{ fontSize:'0.58rem', color:'#374151', flexShrink:0, fontWeight:500 }}>{conv.time}</span>
                          </div>
                          <span style={{ fontSize:'0.67rem', color:'#4B5563', fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                            {conv.username}
                          </span>
                          <span style={{ fontSize:'0.72rem', color: isActive ? '#9CA3AF' : '#525f6e', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:'1.35' }}>
                            {conv.lastMessage || 'No preview available'}
                          </span>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'3px' }}>
                            <div style={{ display:'inline-flex', alignItems:'center', gap:'4px',
                              fontSize:'0.57rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.07em',
                              padding:'2px 7px', borderRadius:'999px',
                              background: badgeBg, color: badgeColor, border: `1px solid ${badgeBorder}` }}>
                              <span style={{ width:5, height:5, borderRadius:'50%', background: dotColor, flexShrink:0, display:'inline-block' }}/>
                              {conv.intent} Intent
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                              {conv.likelihood > 0 && (
                                <span style={{ fontSize:'0.59rem', fontWeight:700, color: likelyColor, background: likelyBg,
                                  border: `1px solid ${badgeBorder}`, borderRadius:'5px', padding:'1px 6px', letterSpacing:'0.02em' }}>
                                  {conv.likelihood}%
                                </span>
                              )}
                              {isActive && (
                                <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                                  <span style={{ width:5, height:5, borderRadius:'50%', background:'#10B981', flexShrink:0,
                                    animation:'activePulse 1.8s ease-in-out infinite', display:'inline-block' }}/>
                                  <span style={{ fontSize:'0.58rem', color:'#10B981', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em' }}>Live</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {!loading && !pageMismatch && !notConnected && conversations.length > 0 && filteredConversations.length === 0 && (
                  <div className="no-results"><p>No results for this filter</p></div>
                )}

                {!loading && !pageMismatch && !notConnected && pagination.hasMore && (
                  <button onClick={() => fetchConversations(pagination.page + 1)}
                    style={{ width:'100%', padding:'9px', background:'none', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, color:'#4B5563', fontSize:12, cursor:'pointer', marginTop:4, transition:'all 0.15s', fontFamily:"'Plus Jakarta Sans', sans-serif" }}
                    onMouseEnter={e => { e.target.style.color='#9CA3AF'; e.target.style.borderColor='rgba(255,255,255,0.12)'; }}
                    onMouseLeave={e => { e.target.style.color='#4B5563'; e.target.style.borderColor='rgba(255,255,255,0.07)'; }}>
                    Load more
                  </button>
                )}
              </div>
            </div>

            {/* Right Detail Panel */}
            <div className={`detail-panel-wrapper ${mobileActiveView === 'list' ? 'mobile-hidden' : ''}`} style={{ minWidth: 0, width: '100%' }}>
              {!loading && pageMismatch && (
                <div className="detail-panel" style={{ alignItems:'center', justifyContent:'center' }}>
                  <PageMismatchState onNavigate={onNavigateToIntegrations}/>
                </div>
              )}
              {!loading && !pageMismatch && notConnected && (
                <div className="detail-panel" style={{ alignItems:'center', justifyContent:'center' }}>
                  <NotConnectedState platform={platformKey} onNavigate={onNavigateToIntegrations}/>
                </div>
              )}
              {!loading && !pageMismatch && !notConnected && conversations.length === 0 && (
                <div className="detail-panel" style={{ alignItems:'center', justifyContent:'center' }}>
                  <NoConversationsState platform={platformKey}/>
                </div>
              )}

              {!pageMismatch && !notConnected && selectedConv && (
                <div className="detail-panel">
                  {/* Header */}
                  <div className="detail-header">
                    <div className="detail-header-left">
                      <button
                        type="button"
                        className="mobile-back-btn"
                        onClick={() => setMobileActiveView('list')}
                        title="Back to conversations"
                      >
                        <ArrowLeft size={17} />
                      </button>
                      <div className="detail-user-icon">
                        <img src={selectedConv.avatar} alt={selectedConv.user}
                          style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }}
                          onError={(e) => { e.target.style.display='none'; }}/>
                      </div>
                      <div className="detail-user-info">
                        <h3 className="detail-user-name">{selectedConv.user}</h3>
                        <p className="detail-user-meta">{selectedConv.username} · {selectedConv.platform.charAt(0).toUpperCase()+selectedConv.platform.slice(1)}</p>
                      </div>
                    </div>
                    <div className="detail-header-right">
                      <div className="likelihood-display">
                        <p className="likelihood-label">Purchase Likelihood</p>
                        <p className={`likelihood-value ${(currentAnalyze.data?.likelihood ?? selectedConv.likelihood) > 74 ? 'high' : 'medium'}`}>
                          {currentAnalyze.loading
                            ? <Loader2 size={13} style={{ animation:'mcSpin 0.8s linear infinite', display:'inline' }}/>
                            : `${currentAnalyze.data?.likelihood ?? selectedConv.likelihood}%`}
                        </p>
                      </div>
                      <div className="header-divider"/>
                      <button className="more-options-btn"><MoreVertical size={17}/></button>
                    </div>
                  </div>

                  {/* Mobile Tab Switcher */}
                  <div className="mobile-detail-tabs">
                    <button
                      type="button"
                      className={`mobile-detail-tab ${mobileDetailTab === 'chat' ? 'active' : ''}`}
                      onClick={() => setMobileDetailTab('chat')}
                    >
                      <MessageCircle size={14} />
                      <span>Messages ({selectedConv.messages.length})</span>
                    </button>
                    <button
                      type="button"
                      className={`mobile-detail-tab ${mobileDetailTab === 'insights' ? 'active' : ''}`}
                      onClick={() => setMobileDetailTab('insights')}
                    >
                      <Brain size={14} />
                      <span>Live Insights</span>
                      {(currentAnalyze.data?.likelihood ?? selectedConv.likelihood) > 0 && (
                        <span className="mobile-tab-badge">
                          {currentAnalyze.data?.likelihood ?? selectedConv.likelihood}%
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Body */}
                  <div className="detail-body">
                    {/* Chat */}
                    <div className={`chat-thread ${mobileDetailTab === 'insights' ? 'mobile-tab-hidden' : ''}`}>
                      {loadingMessages && (
                        <div style={{ display:'flex', justifyContent:'center', padding:'24px 0' }}>
                          <Loader2 size={17} style={{ animation:'mcSpin 0.8s linear infinite', color:'#374151' }}/>
                        </div>
                      )}
                      {!loadingMessages && selectedConv.messages.map(msg => {
                        const isBot = msg.sender === 'revexia';
                        // Display the sender's name: page name for bot, user name for customer
                        const senderLabel = isBot
                          ? (msg.fromName || 'Page')
                          : (msg.fromName || selectedConv.user);

                        return (
                          <div key={msg.id} style={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: isBot ? 'flex-end' : 'flex-start',
                            gap: '4px', animation: 'fadeSlideIn 0.18s ease',
                          }}>
                            {/* Timestamp */}
                            {msg.timestamp && (
                              <span style={{ fontSize:'0.58rem', color:'#374151', padding:'0 4px',
                                alignSelf: isBot ? 'flex-end' : 'flex-start' }}>
                                {msg.timestamp}
                              </span>
                            )}
                            {/* Bubble */}
                            <div style={{
                              maxWidth: '78%', padding: '0.6rem 0.85rem',
                              borderRadius: isBot ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
                              background: isBot
                                ? 'linear-gradient(135deg, rgba(99,102,241,0.28), rgba(139,92,246,0.18))'
                                : 'rgba(255,255,255,0.07)',
                              border: isBot
                                ? '1px solid rgba(139,92,246,0.25)'
                                : '1px solid rgba(255,255,255,0.1)',
                              boxShadow: isBot
                                ? '0 2px 12px rgba(99,102,241,0.12)'
                                : '0 1px 4px rgba(0,0,0,0.2)',
                            }}>
                              <p style={{ fontSize:'0.78rem', color: isBot ? '#e2e8f0' : '#d1d5db',
                                margin:0, lineHeight:'1.58', wordBreak:'break-word' }}>
                                {msg.text}
                              </p>
                              {msg.tags && (
                                <div style={{ display:'flex', flexWrap:'wrap', gap:'0.28rem', marginTop:'0.38rem' }}>
                                  {msg.tags.map(tag => (
                                    <div key={tag} style={{ display:'flex', alignItems:'center', gap:3, fontSize:'0.58rem', color:'#6B7280', background:'rgba(255,255,255,0.06)', padding:'0.13rem 0.38rem', borderRadius:4 }}>
                                      <Tag size={9}/> {tag}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            {/* Sender label — shows actual name from from.name */}
                            <span style={{ fontSize:'0.56rem', color:'#374151', padding:'0 4px',
                              fontWeight:600, letterSpacing:'0.04em', textTransform:'uppercase' }}>
                              {senderLabel}
                            </span>
                          </div>
                        );
                      })}
                      {!loadingMessages && selectedConv.messages.length === 0 && (
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:'0.6rem', color:'#374151' }}>
                          <MessageCircle size={32} style={{ opacity:0.3 }}/>
                          <p style={{ fontSize:13, margin:0 }}>No messages yet</p>
                        </div>
                      )}
                    </div>

                    {/* Analyze sidebar */}
                    <aside className={`insights-sidebar ${mobileDetailTab === 'chat' ? 'mobile-tab-hidden' : ''}`}>
                      <AnalyzePanel
                        convId={selectedConv.id}
                        analysis={currentAnalyze.data}
                        loading={currentAnalyze.loading}
                        notConnected={analyzeNotConnected}
                      />
                    </aside>
                  </div>

                  {/* Journey */}
                  {Array.isArray(selectedConv.journey) && selectedConv.journey.length > 0 && (
                    <div className="journey-timeline">
                      <div className="journey-timeline-header">
                        <h4 className="journey-title">Customer Journey Path</h4>
                        <div className="optimized-badge"><span>Optimized Flow</span><CheckCircle2 size={11}/></div>
                      </div>
                      <div className="journey-steps">
                        <div className="journey-progress-line"/>
                        {selectedConv.journey.map((step, i) => (
                          <div key={i} className="journey-step">
                            <div className={`journey-step-icon ${step.status}`}>
                              {step.status==='completed' ? <CheckCircle2 size={13}/> : <div className="journey-step-dot"/>}
                            </div>
                            <div className="journey-step-info">
                              <p className={`journey-step-name ${step.status}`}>{step.step}</p>
                              {step.time && <p className="journey-step-time">{step.time}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!pageMismatch && !notConnected && !selectedConv && conversations.length > 0 && (
                <div className="detail-panel" style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <div style={{ textAlign:'center', color:'#374151' }}>
                    <User size={36} style={{ margin:'0 auto 10px', opacity:0.25 }}/>
                    <p style={{ fontSize:13, margin:0 }}>Select a conversation to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default DMActivity;